import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { getOrCreateUser, getUserByClerkId, deductCredits } from "@/db/users";
import type { User } from "@/db/schema";

/**
 * Hook to get/sync the current user with our database
 * This creates a user record if it doesn't exist
 */
export function useCurrentUser() {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();

  const query = useQuery({
    queryKey: ["currentUser", clerkUser?.id],
    queryFn: async (): Promise<User | null> => {
      if (!clerkUser) return null;

      const user = await getOrCreateUser(
        clerkUser.id,
        clerkUser.emailAddresses[0]?.emailAddress || "",
        clerkUser.firstName || undefined
      );

      return user;
    },
    enabled: isClerkLoaded && !!clerkUser,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    user: query.data,
    isLoading: !isClerkLoaded || query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to check and deduct credits
 */
export function useCredits() {
  const queryClient = useQueryClient();
  const { user: clerkUser } = useUser();
  const { user } = useCurrentUser();

  const deductMutation = useMutation({
    mutationFn: async (amount: number) => {
      if (!clerkUser) throw new Error("Not authenticated");
      const result = await deductCredits(clerkUser.id, amount);
      if (!result) throw new Error("Insufficient credits");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });

  return {
    credits: user?.creditsRemaining ?? 0,
    subscriptionTier: user?.subscriptionTier ?? "free",
    deductCredits: deductMutation.mutate,
    isDeducting: deductMutation.isPending,
    hasEnoughCredits: (amount: number) => (user?.creditsRemaining ?? 0) >= amount,
  };
}

/**
 * Hook to get user by Clerk ID (for admin purposes)
 */
export function useUserByClerkId(clerkId: string | undefined) {
  return useQuery({
    queryKey: ["user", clerkId],
    queryFn: () => (clerkId ? getUserByClerkId(clerkId) : null),
    enabled: !!clerkId,
  });
}
